import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18n from '../../i18n';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import { selectSaveResult } from '../../redux/selectors/exportSelectorPaths';

export const isValidationResult = (
  value: ValidationInput
): value is ValidationResult => {
  return (
    !!value &&
    !Array.isArray(value) &&
    typeof value === "object" &&
    "errors" in value &&
    "warnings" in value
  );
};



export type ValidationResult = {
  errors: FormError[];
  warnings: FormError[];
};

// Accept BOTH shapes
export type ValidationInput =
  | FormError[]
  | ValidationResult
  // eslint-disable-next-line
  | Record<string, any>[];

// Define the shape of the error object with only the required fields from Spectral API response
export interface FormError {
  primaryKey: string;
  message: string;
  messageFr: string;
  level: string;
  componentId?: string;
  detail?: SpectralDetail;
}

// Context Props
interface FormErrorContextProps {
  errors: FormError[];  // Array of form validation errors
  addError: (error: FormError) => void;
  addErrors: (errors: FormError[]) => void;
  removeError: (primaryKey: string | null) => void;
  clearErrors: () => void;
  updateErrors: (errors: FormError[]) => void;
}

// type for spectral details block
export type SpectralDetail = {
  componentId?: string | string[];
  // eslint-disable-next-line
  [key: string]: any;
};

// Create the context with a default value of undefined
const FormErrorContext = createContext<FormErrorContextProps | undefined>(undefined);

// Custom hook
export const useFormErrorContext = (): FormErrorContextProps => {
  const context = useContext(FormErrorContext);
  if (!context) {
    throw new Error('useFormErrorContext must be used within a FormErrorProvider');
  }
  return context;
};

// Provider props
interface FormErrorProviderProps {
  children: ReactNode;
}

export const FormErrorProvider = (props: FormErrorProviderProps) => {
  // Initial state of the form error context
  const [errors, setErrors] = useState<FormError[]>([]);

  // Add an error to the list
  const addError = useCallback((error: FormError) => {
    setErrors((prevErrors) => [...prevErrors, error]);
  }, []);

  const addErrors = useCallback((errors: FormError[]) => {
    setErrors((prevErrors) => [...prevErrors, ...errors]);
  }, []);

  // Remove an error based on the primaryKey
  const removeError = (primaryKey: string | null) => {
    setErrors((prevErrors) => prevErrors.filter((error) => error.primaryKey !== primaryKey));
  };

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Set errors
  const updateErrors = useCallback((newErrors: FormError[]) => {
    setErrors(newErrors);
  }, []);

  return (
    <FormErrorContext.Provider value={{ errors, addError, addErrors, removeError, clearErrors, updateErrors }}>
      {props.children}
    </FormErrorContext.Provider>
  );
};

const normalizeResponse = (apiResponse: ValidationInput): FormError[] => {
  if (!apiResponse) return [];

  if (Array.isArray(apiResponse)) {
    return apiResponse as FormError[];
  }

  if (isValidationResult(apiResponse)) {
    return apiResponse.errors;
  }

  return [];
};

// Custom hook to parse Spectral Validation Response into FormErrorContext
export const useFormErrorValidationResponse = (apiResponse: ValidationInput) => {
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const { addErrors, updateErrors } = useFormErrorContext();

  useEffect(() => {
    const normalized = normalizeResponse(apiResponse);

    if (normalized.length === 0) return;

    const newErrors: FormError[] = [];

    normalized.forEach((error) => {
      if (error.level === "error"){
        const {
          primaryKey,
          componentId,
          message,
          messageFr,
          level,
          detail
        } = error;
  
        if (primaryKey === null) {
          newErrors.push({
            primaryKey,
            message,
            messageFr: messageFr,
            level
          });
        } else {
          const parts = primaryKey.split(".");
  
          // CASE 1: direct componentId
          if (componentId) {
            newErrors.push({
              primaryKey: componentId + "." + parts[1],
              message,
              messageFr: messageFr,
              level
            });
  
            // CASE 2: detail.componentId (string OR array)
          } else if (detail?.componentId) {
            const componentIds = Array.isArray(detail.componentId)
              ? detail.componentId
              : [detail.componentId];
  
            componentIds.forEach((compId) => {
              let updatedKey = "";
  
              if (parts.length === 4) {
                updatedKey = `${parts[0]}.${parts[1]}.${parts[2]}.${compId}.${parts[3]}`;
              } else if (parts.length === 2) {
                updatedKey = `${compId}.${parts[1]}`;
              } else {
                updatedKey = `${parts[0]}.${parts[1]}.${compId}.${parts[2]}`;
              }
  
              newErrors.push({
                primaryKey: updatedKey,
                message,
                messageFr: messageFr,
                level
              });
            });
  
            // CASE 3: fallback
          } else {
            newErrors.push({
              primaryKey,
              message,
              messageFr: messageFr,
              level
            });
          }
        }
      }
    });

    // single state update (prevents infinite loop)
    if (saveResultState.statuses.length > 1) {
      addErrors(newErrors);
    } else {
      updateErrors(newErrors)
    }
    
  }, [addErrors, apiResponse, saveResultState.statuses.length, updateErrors]);
}


export type MatchingStrategy = (primaryKey: string, key: string) => boolean

export const defaultMatcher: MatchingStrategy = (primaryKey, key) => {
  const parts = primaryKey.split(".")
  return parts.length > 1 && parts[1].toLowerCase() === key.toLowerCase()
}

export const incomeAttributeMatcher: MatchingStrategy = (primaryKey, key) => {
  const match = primaryKey.match(/\[(\d+)\]\.value/)
  return match ? match[1] === key : false
}

export const incomeAttributePercentMatcher: MatchingStrategy = (primaryKey, key) => {
  const match = primaryKey.match(/\[(\d+)\]\.percent/)
  return match ? match[1] === key : false
}

export const incomeAttributeDescMatcher: MatchingStrategy = (primaryKey, key) => {
  const match = primaryKey.match(/\[(\d+)\]\.description/)
  return match ? match[1] === key : false
}

export const exactMatcher: MatchingStrategy = (primaryKey, key) => {
  return primaryKey.toLowerCase() === key.toLowerCase()
}

export const getFormErrorByName = (errors: FormError[], key: string, matcher: MatchingStrategy = defaultMatcher) => {
  return errors.find(error => {
    if (error.primaryKey === null || key === undefined) return false
    return matcher(error.primaryKey, key)
  })
}

export const getFormErrorsByName = (errors: FormError[], key: string, matcher: MatchingStrategy = defaultMatcher) => {
  return errors.filter(error => {
    if (error.primaryKey === null || key === undefined) return false
    return matcher(error.primaryKey, key)
  })
}

export const getFormErrorMessageByName = (errors: FormError[], key: string, matcher?: MatchingStrategy) => {
  const error = getFormErrorByName(errors, key, matcher)
  return error ? (i18n.language === 'fr' ? error.messageFr : error.message) : undefined
}

export const getFormErrorMessageByError = (error: FormError) => {
  return (i18n.language === 'fr' ? error.messageFr : error.message)
}

export const hasError = (errors: FormError[], key: string, matcher?: MatchingStrategy) => {
  return getFormErrorByName(errors, key, matcher) !== undefined
}

